import { consola } from 'consola'
import { Capacitor } from '@capacitor/core'
import { useModalStore , modalStateEnum } from '@shared/model'
import { usePushNotificationStorage } from '@features/notifications'


// Глобальный флаг для предотвращения повторной инициализации
let isInitializing = false

// Пуш-промпт новичка показываем строго в ФИНАЛЕ онбординга (после того как пользователь
// добавит адрес доставки ИЛИ нажмёт «Не сейчас»), а НЕ сразу после авторизации.
// Пока флаг взведён — авто-показ из initPushNotificationsAfterAuth подавляется; промпт
// всплывает, когда закроется последняя модалка онбординг-цепочки (см. usePushNotificationInit).
let onboardingPushPending = false

export function usePushNotificationSettings() {
  const modalStore = useModalStore()
  const storage = usePushNotificationStorage()

  /**
   * Проверяет, запущено ли приложение в мобильном режиме (Capacitor)
   */
  const isMobileApp = (): boolean => Boolean(process.client && Capacitor.isNativePlatform())

  /**
   * Проверяет нужно ли показать запрос на push уведомления
   */
  const shouldAskForPushPermission = async (): Promise<boolean> => {
    if (!process.client) return false

    // Push уведомления работают только в мобильном приложении
    if (!isMobileApp()) {
      consola.debug('🔔 [PUSH-SETTINGS] Not a mobile app, skipping push notification setup')
      return false
    }

    // Проверяем, спрашивали ли уже пользователя
    const hasAsked = await storage.wasPushAsked()
    const userEnabled = await storage.isPushEnabled()

    // Если пользователь уже дал ответ, не спрашиваем повторно
    if (hasAsked || userEnabled) {
      return false
    }

    return true
  }

  /**
   * Показывает модальное окно для запроса разрешения на push уведомления
   */
  const showPushPermissionModal = () => {
    // Проверяем, что мы в мобильном приложении
    if (!isMobileApp()) {
      consola.debug('🔔 [MODAL DEBUG] Not a mobile app, skipping modal display')
      return
    }

    consola.debug('🔔 [MODAL DEBUG] Attempting to show push notification permission modal')
    consola.debug('🔔 [MODAL DEBUG] Modal state enum:', modalStateEnum.PushNotificationPermission)
    consola.debug('🔔 [MODAL DEBUG] Modal store:', modalStore)

    try {
      modalStore.open(modalStateEnum.PushNotificationPermission)
      consola.debug('🔔 [MODAL DEBUG] Modal open command sent successfully')
    } catch (error) {
      consola.error('🔔 [MODAL DEBUG] Error opening modal:', error)
    }
  }

  /**
   * Помечает, что показ пуш-промпта нужно отложить до конца онбординга новичка.
   * Вызывается сразу после успешной регистрации (см. useAuthStore.register).
   */
  const markOnboardingPushPending = () => {
    onboardingPushPending = true
  }

  /**
   * Снимает отложенный показ пуш-промпта (например, если пользователь ушёл из
   * подтверждения телефона, так и не завершив регистрацию).
   */
  const clearOnboardingPushPending = () => {
    onboardingPushPending = false
  }

  /** Ждём ли мы конца онбординга, чтобы показать пуш-промпт. */
  const isOnboardingPushPending = (): boolean => onboardingPushPending

  /**
   * Показывает пуш-промпт в финале онбординга — после закрытия последней модалки
   * онбординг-цепочки (адрес добавлен / нажато «Не сейчас»). Гейтится теми же
   * проверками, что и авто-показ (мобильное приложение + пользователя ещё не спрашивали).
   */
  const showOnboardingPushPrompt = async (): Promise<void> => {
    onboardingPushPending = false

    if (!(await shouldAskForPushPermission())) return

    // Небольшая задержка, чтобы закрывшаяся модалка онбординга/карты успела
    // размонтироваться до открытия пуш-модалки (иначе DynamicModal моргает).
    setTimeout(() => {
      consola.debug('🔔 [DEBUG] Показываем пуш-промпт после онбординга')
      showPushPermissionModal()
    }, 300)
  }

  /**
   * Проверяет включены ли push уведомления пользователем
   */
  const isPushNotificationsEnabled = async (): Promise<boolean> => {
    if (!process.client) return false
    return await storage.isPushEnabled()
  }

  /**
   * Возвращает системный статус разрешений (granted | denied | prompt)
   */
  const getSystemPermissionStatus = async (): Promise<'granted' | 'denied' | 'prompt'> => {
    if (!process.client || !isMobileApp()) return 'prompt'

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      const permStatus = await PushNotifications.checkPermissions()
      return permStatus.receive as 'granted' | 'denied' | 'prompt'
    } catch (error) {
      consola.warn('🔔 [PERM] Failed to check system permissions:', error)
      return 'prompt'
    }
  }

  /**
   * Запрашивает системное разрешение (iOS покажет системный алерт)
   */
  const requestSystemPermission = async (): Promise<'granted' | 'denied' | 'prompt'> => {
    if (!process.client || !isMobileApp()) return 'prompt'

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      const permResult = await PushNotifications.requestPermissions()
      return permResult.receive as 'granted' | 'denied' | 'prompt'
    } catch (error) {
      consola.warn('🔔 [PERM] Failed to request permissions:', error)
      return 'prompt'
    }
  }

  /**
   * Открывает системные настройки приложения (для ручного включения уведомлений)
   */
  const openSystemSettings = async (): Promise<void> => {
    if (!process.client || !isMobileApp()) return

    try {
      const platform = Capacitor.getPlatform()

      if (platform === 'ios') {
        // Для iOS используем прямой URL настроек приложения
        // UIApplication.openSettingsURLString = "app-settings:"
        const settingsUrl = 'app-settings:'

        // Пробуем через Browser plugin
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.open({ url: settingsUrl })
          consola.debug('✅ [PERM] Opened settings via Browser plugin')
          return
        } catch (browserError) {
          consola.warn('🔔 [PERM] Browser plugin failed, trying window.location:', browserError)
        }

        // Fallback: прямое изменение window.location
        if (typeof window !== 'undefined') {
          window.location.href = settingsUrl
          consola.debug('✅ [PERM] Opened settings via window.location')
          
        }
      } else if (platform === 'android') {
        // Для Android используем App.openSettings
        try {
          const { App } = await import('@capacitor/app')
          await App.openSettings()
          consola.debug('✅ [PERM] Opened settings via App.openSettings')
          
        } catch (appError) {
          consola.warn('🔔 [PERM] App.openSettings failed:', appError)
        }
      }
    } catch (error) {
      consola.warn('🔔 [PERM] Failed to open system settings:', error)
    }
  }

  /**
   * Включает push уведомления
   * При включении использует существующий токен (если есть) или получает новый
   */
  const enablePushNotifications = async (): Promise<boolean> => {
    // Проверяем, что мы в мобильном приложении
    if (!isMobileApp()) {
      consola.debug('🔔 [ENABLE] Not a mobile app, skipping push notification enable')
      return false
    }

    try {
      consola.debug('🔔 [ENABLE] Attempting to enable push notifications...')

      // Проверяем, есть ли сохранённый токен
      const existingToken = await storage.getPushToken()
      if (existingToken) {
        consola.debug('🔔 [ENABLE] Found existing token:', existingToken.slice(0, 30) + '...')
        consola.debug('ℹ️ [ENABLE] Will reuse existing token (no need to regenerate)')

        // Отправляем существующий токен на сервер (если он там ещё не зарегистрирован)
        try {
          const apiProfile = await import('~/entities/user')
          const platform = Capacitor.getPlatform()
          await apiProfile.default.addPushToken({
            token: existingToken,
            platform: platform === 'ios' ? 'ios' : 'android',
          })
          consola.debug('✅ [ENABLE] Existing token sent to server')
        } catch (error) {
          // Игнорируем ошибку если токен уже есть на сервере
          consola.debug('ℹ️ [ENABLE] Token already on server or error (will continue):', error)
        }

        // Устанавливаем флаг включения
        await storage.setPushEnabled(true)
        await storage.setPushAsked(true)
        consola.debug('✅ [ENABLE] Push notifications enabled successfully (using existing token)')
        return true
      }

      // Если токена нет, инициализируем push для получения нового токена
      consola.debug('🔔 [ENABLE] No existing token found, initializing push notifications...')

      if (typeof globalThis.initPushNotificationsAfterAuth === 'function') {
        const success = await globalThis.initPushNotificationsAfterAuth()
        consola.debug('🔔 [ENABLE] initPushNotificationsAfterAuth result:', success)

        if (success) {
          await storage.setPushEnabled(true)
          await storage.setPushAsked(true)
          consola.debug('🔔 [ENABLE] Push notifications enabled successfully')
          return true
        }
      } else {
        consola.error('🔔 [ENABLE] initPushNotificationsAfterAuth function not available')
      }

      await storage.setPushEnabled(false)
      await storage.setPushAsked(true)
      consola.debug('🔔 [ENABLE] Failed to enable push notifications')
      return false
    } catch (error) {
      consola.error('🔔 [ENABLE] Error enabling push notifications:', error)
      return false
    }
  }

  /**
   * Отключает push уведомления
   * ВАЖНО: Токен НЕ удаляется из БД и локального хранилища, только сохраняется флаг отключения
   * Это позволяет быстро переподключать уведомления без пересоздания токена (особенно важно для iOS)
   * На сервере нужно проверять флаг isPushEnabled перед отправкой уведомлений
   */
  const disablePushNotifications = async (): Promise<boolean> => {
    // Проверяем, что мы в мобильном приложении
    if (!isMobileApp()) {
      consola.debug('🔔 [DISABLE] Not a mobile app, skipping push notification disable')
      return false
    }

    consola.debug('🔔 [DISABLE] Disabling push notifications (token will be kept for quick re-enable)...')

    try {
      // Получаем текущий токен (если есть) - только для логирования
      const currentToken = await storage.getPushToken()

      if (currentToken) {
        consola.debug('🔔 [DISABLE] Token found:', currentToken.slice(0, 30) + '...')
        consola.debug('ℹ️ [DISABLE] Token will NOT be deleted - kept for quick re-enable')
        consola.debug('ℹ️ [DISABLE] Server should check isPushEnabled flag before sending notifications')
      } else {
        consola.debug('⚠️ [DISABLE] No token found in storage')
      }

      // Сохраняем только флаг отключения - токен остаётся в хранилище и на сервере
      await storage.setPushEnabled(false)
      await storage.setPushAsked(true)
      consola.debug('✅ [DISABLE] Push settings updated (disabled flag set)')

      // НЕ сбрасываем состояние инициализации - токен сохраняется для быстрого переподключения
      // НЕ удаляем токен из Firebase - он будет использован при повторном включении

      consola.debug('✅ [DISABLE] Push notifications disabled successfully (token preserved)')
      return true
    } catch (error) {
      consola.error('❌ [DISABLE] Error disabling push notifications:', error)
      return false
    }
  }

  /**
   * Переключает состояние push уведомлений
   */
  const togglePushNotifications = async (): Promise<boolean> => {
    const isEnabled = await isPushNotificationsEnabled()

    return await (isEnabled ? disablePushNotifications() : enablePushNotifications());
  }

  /**
   * Проверяет, открыта ли модалка авторизации
   */
  const isAuthModalOpen = (): boolean => {
    // Вызывается только после isMobileApp() в init; на SSR сюда не попадаем
    // Проверяем открыта ли модалка и это модалка авторизации
    if (!modalStore.isOpen) return false

    const authModals = [
      modalStateEnum.Login,
      modalStateEnum.Registration,
      modalStateEnum.NumberConfirmation,
      modalStateEnum.CodeFromEmail,
      modalStateEnum.ForgotPassword,
      modalStateEnum.ResetPassword,
    ]

    return authModals.includes(modalStore.currentState)
  }

  /**
   * Ждет закрытия модалки авторизации
   */
  function waitForAuthModalToClose(): Promise<void> {
    if (!isAuthModalOpen()) return Promise.resolve()

    consola.debug('🔔 [DEBUG] Auth modal is open, waiting for it to close...')

    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isAuthModalOpen()) {
          consola.debug('🔔 [DEBUG] Auth modal closed, continuing...')
          clearInterval(checkInterval)
          resolve()
        }
      }, 100) // Проверяем каждые 100ms

      // Таймаут на случай если модалка не закроется
      setTimeout(() => {
        clearInterval(checkInterval)
        consola.debug('🔔 [DEBUG] Timeout waiting for auth modal, continuing anyway...')
        resolve()
      }, 5000) // Максимум 5 секунд ждем
    })
  }

  /**
   * Инициализация push уведомлений после авторизации
   */
  const initPushNotificationsAfterAuth = async () => {
    consola.debug('🔔 [DEBUG] Starting initPushNotificationsAfterAuth...')

    // Предотвращаем повторный вызов если уже выполняется
    if (isInitializing) {
      consola.debug('🔔 [DEBUG] Already initializing, skipping duplicate call')
      return
    }

    // Проверяем, что мы в мобильном приложении
    if (!isMobileApp()) {
      consola.debug('🔔 [DEBUG] Not a mobile app, skipping push notification initialization')
      return
    }

    // Устанавливаем флаг
    isInitializing = true

    try {
      // Ждем закрытия модалки авторизации (если открыта)
      await waitForAuthModalToClose()

      // Проверяем наше сохраненное состояние
      const isEnabled = await isPushNotificationsEnabled()
      consola.debug('🔔 [DEBUG] isPushNotificationsEnabled:', isEnabled)

      // Проверяем системное состояние iOS/Android
      let systemPermissionStatus = 'prompt'
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications')
        const permStatus = await PushNotifications.checkPermissions()
        systemPermissionStatus = permStatus.receive
        consola.debug('🔔 [DEBUG] System permission status:', systemPermissionStatus)
      } catch (error) {
        consola.warn('🔔 [DEBUG] Failed to check system permissions:', error)
      }

      // Если пользователь ранее включил И системное разрешение уже получено - просто регистрируем
      if (isEnabled && systemPermissionStatus === 'granted') {
        consola.debug('🔔 User previously enabled push notifications and system permission granted, initializing...')
        await enablePushNotifications()
        return
      }

      // Если пользователь включал, но системное разрешение НЕ получено - показываем модалку
      if (isEnabled && systemPermissionStatus === 'prompt') {
        consola.debug('🔔 User enabled push but system permission not granted, showing modal...')
        // Сбрасываем наше состояние, т.к. системного разрешения нет
        await storage.setPushEnabled(false)
        // Показываем модалку с задержкой
        setTimeout(() => {
          consola.debug('🔔 [DEBUG] Showing push permission modal now!')
          showPushPermissionModal()
        }, 1000)
        return
      }

      // Если пользователя еще не спрашивали, показываем модальное окно
      const shouldAsk = await shouldAskForPushPermission()
      consola.debug('🔔 [DEBUG] shouldAskForPushPermission:', shouldAsk)

      if (shouldAsk) {
        // Сразу после регистрации НЕ показываем промпт: ждём финала онбординга
        // (адрес добавлен / нажато «Не сейчас»). Показ инициирует usePushNotificationInit
        // при закрытии последней модалки онбординг-цепочки.
        if (onboardingPushPending) {
          consola.debug('🔔 [DEBUG] Промпт отложен до конца онбординга — пропускаем авто-показ')
          return
        }

        consola.debug('🔔 [DEBUG] Will show modal in 1 second...')
        // Небольшая задержка чтобы UI успел загрузиться и модалка авторизации точно закрылась
        setTimeout(() => {
          consola.debug('🔔 [DEBUG] Showing push permission modal now!')
          showPushPermissionModal()
        }, 1000)
      } else {
        consola.debug('🔔 [DEBUG] Not showing modal - user was already asked or has preference set')
      }
    } catch (error) {
      consola.error('🔔 [DEBUG] Error in initPushNotificationsAfterAuth:', error)
    } finally {
      // Сбрасываем флаг после завершения (успех или ошибка)
      // Задержка чтобы дать время модалке появиться
      setTimeout(() => {
        isInitializing = false
        consola.debug('🔔 [DEBUG] Initialization flag reset')
      }, 2000)
    }
  }

  return {
    shouldAskForPushPermission,
    showPushPermissionModal,
    markOnboardingPushPending,
    clearOnboardingPushPending,
    isOnboardingPushPending,
    showOnboardingPushPrompt,
    isPushNotificationsEnabled,
    enablePushNotifications,
    disablePushNotifications,
    togglePushNotifications,
    initPushNotificationsAfterAuth,
    getSystemPermissionStatus,
    requestSystemPermission,
    openSystemSettings,
  }
}
